/**
 * The start sheet of Create → "New story" (spec 024 D-12, D-16, spec 002): a
 * wrapper of the shared `StartSheet` (a large modal) with "✨ Describe your
 * training" / "Start with AI" on top and a "Blank story" card below an "or"
 * divider. Opening it creates nothing.
 *
 * - Start with AI creates the story in the current folder, named after the
 *   brief's first line (D-4), stores the fixed brief text as its
 *   `pendingBrief` and opens it in the studio; the story editor sends it as
 *   the first turn.
 * - Blank story creates "Untitled Story" (unique among the sibling stories)
 *   and opens it in the studio.
 * - With the assistant unavailable, only "Blank story" works.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../../api/client';
import { useAssistantStore } from '../../stores/assistantStore';
import { uniqueName } from './organiser';
import StartSheet, { NAME_FROM_BRIEF_MAX, nameFromBrief, type StartSheetValues } from './StartSheet';

export { OBJECTIVE_MAX, AUDIENCE_MAX } from './StartSheet';
export const STORY_NAME_FROM_BRIEF_MAX = NAME_FROM_BRIEF_MAX;
export const BLANK_STORY_NAME = 'Untitled Story';

export type BriefLength = 'short' | 'medium' | 'long';
export const LENGTH_SCENES: Readonly<Record<BriefLength, string>> = {
  short: '3–4 scenes',
  medium: '5–7 scenes',
  long: '8–12 scenes',
};

/** The Length row's options: "Short (3–4 scenes)", "Medium (5–7 scenes)", "Long (8–12 scenes)". */
export const LENGTH_OPTIONS: ReadonlyArray<{ value: BriefLength; label: string }> = [
  { value: 'short', label: `Short (${LENGTH_SCENES.short})` },
  { value: 'medium', label: `Medium (${LENGTH_SCENES.medium})` },
  { value: 'long', label: `Long (${LENGTH_SCENES.long})` },
];

export const START_SHEET_TEXT = {
  title: 'New story',
  heading: '✨ Describe your training',
  placeholder: 'A new hire handles an angry caller and calms them down before offering a fix…',
  audiencePlaceholder: 'Who is it for? For example, first-week support staff',
  unavailable: "The assistant isn't set up",
  blankTitle: 'Blank story',
  blankHint: 'Start from an empty map',
} as const;

export interface Brief { brief: string; objective?: string; audience?: string; length: BriefLength }

/** The brief's first non-empty line, trimmed to 60 characters at a word boundary (D-4); the shared `nameFromBrief` (029). */
export const storyNameFromBrief = nameFromBrief;

/** The fixed first message of a story started with AI, one line per field, empty fields left out. */
export function briefMessage({ brief, objective, audience, length }: Brief): string {
  const lines = ['Build a new story.', `Brief: ${brief.trim()}`];
  if (objective?.trim()) lines.push(`Learning objective: ${objective.trim()}`);
  if (audience?.trim()) lines.push(`Audience: ${audience.trim()}`);
  lines.push(`Length: ${length} (${LENGTH_SCENES[length]}).`);
  return lines.join('\n');
}

export interface StoryStartSheetProps {
  show: boolean;
  onHide: () => void;
  /** The folder the Create menu was opened in (null: Home). */
  folderId: number | null;
  /** The names of the stories in that folder, for unique names. */
  siblingStoryNames: Array<string | null | undefined>;
  /** Passed as router state, like opening any card (002). */
  from?: string;
}

const errorOf = (res: { success: boolean; error?: string } | null | undefined) =>
  (res && !res.success && res.error) || 'Create failed';

const isLength = (v: string | undefined): v is BriefLength => v === 'short' || v === 'medium' || v === 'long';

export const StoryStartSheet: React.FC<StoryStartSheetProps> = ({ show, onHide, folderId, siblingStoryNames, from }) => {
  const navigate = useNavigate();

  const open = (id: number) => navigate(`/story/${id}?view=studio`, from ? { state: { from } } : undefined);

  /** Creates the story; a failure throws the sheet's error text. */
  const create = async (name: string): Promise<number> => {
    let res: Awaited<ReturnType<typeof apiClient.createStory>>;
    try {
      res = await apiClient.createStory({ name, folderId });
    } catch (e) {
      throw new Error(`Could not create a story: ${e instanceof Error ? e.message : 'Create failed'}`);
    }
    if (!res.success) throw new Error(`Could not create a story: ${errorOf(res)}`);
    return res.data.id;
  };

  const startWithAI = async ({ brief, objective, audience, length }: StartSheetValues) => {
    const id = await create(uniqueName(storyNameFromBrief(brief), siblingStoryNames));
    useAssistantStore.getState().setPendingBrief(id, briefMessage({
      brief, objective, audience, length: isLength(length) ? length : 'medium',
    }));
    onHide();
    open(id);
  };

  const blankStory = async () => {
    const id = await create(uniqueName(BLANK_STORY_NAME, siblingStoryNames));
    onHide();
    open(id);
  };

  return (
    <StartSheet
      show={show}
      onHide={onHide}
      testIdPrefix="story-start"
      title={START_SHEET_TEXT.title}
      heading={START_SHEET_TEXT.heading}
      placeholder={START_SHEET_TEXT.placeholder}
      audiencePlaceholder={START_SHEET_TEXT.audiencePlaceholder}
      lengthOptions={LENGTH_OPTIONS}
      defaultLength="medium"
      blankTitle={START_SHEET_TEXT.blankTitle}
      blankHint={START_SHEET_TEXT.blankHint}
      onStart={startWithAI}
      onBlank={blankStory}
    />
  );
};

export default StoryStartSheet;
